<template>
  <div
    class="text-card-item"
    @click.stop="handleClickTextCardComponent"
    :class="{ clickable: hasTextCardItemComponentAction }"
  >
    <div class="text-card-item__header">
      <div
        class="text-card-item__dot"
        :class="classIcon"
        :style="styleVarsIcon"
      >
        <img :src="iconSrc || NOICON" alt="icon" />
      </div>
      <div
        class="text-card-item__title"
        :class="classTitle"
        :style="styleVarsTitle"
        @click.stop="
          props.textCardItemSetup.action?.title_link ||
          props.textCardItemSetup.action?.title_popup_id
            ? handleClickHeaderTextCardItem()
            : handleClickTextCardComponent()
        "
      >
        {{ title }}
      </div>
    </div>

    <div class="text-card-item__body">
      <div
        class="text-card-item__line-block"
        :class="classLine"
        :style="styleVarsLine"
      >
        <div class="text-card-item__line"></div>
      </div>
      <div
        class="text-card-item__content"
        :style="styleVarsContent"
        v-html="markdownToHTML(content)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, onMounted } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";
import mushroom from "cem-probe-api";
import { Filter } from "mushroomjs";
import NOICON from "../assets/icon/none-icon.svg";
import MethodService from "../service/MethodService";

const props = defineProps({
  textCardItemSetup: {
    type: Object,
    required: true,
  },
  textCardItemData: {
    type: Array,
    required: true,
  },
  onTextCardClick: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(["showDialogDashboardPopupFromTextCardItem"]);
const iconSrc = ref(null);
const title = computed(() => props.textCardItemData?.[0] ?? "");
const content = computed(() => props.textCardItemData?.[2] ?? "");
const scoreValue = computed(() => props.textCardItemData?.[3] ?? null); // truyền cho link dashboard popup

const hasTextCardItemComponentAction = computed(() => {
  return !!(
    props.textCardItemSetup.action?.link ||
    props.textCardItemSetup.action?.popup_id
  );
});
const renderIconFileId = async (codeIcon) => {
  try {
    const response = await mushroom.card_item_icon.listAsync({
      filters: Filter.eq("code", codeIcon).build(),
    });
    if (response.result) return response.result[0]?.file_id || null;
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
  return null;
};

const renderIconSrc = (fileId) =>
  fileId ? mushroom.$file.linkBuilder.thumb.id(fileId).build() : null;

const classTitle = computed(() => ({
  "text-card-item__title--clickable":
    props.textCardItemSetup.action?.title_link ||
    props.textCardItemSetup.action?.title_popup_id,
}));

const styleVarsTitle = computed(() => ({
  "--title-font-size": `${props.textCardItemSetup?.title?.fontSize ?? 14}px`,
  "--title-color": props.textCardItemSetup?.title?.color || "#000",
  "--title-font-weight": props.textCardItemSetup?.title?.bold
    ? "bold"
    : "normal",
}));

const styleVarsContent = computed(() => ({
  "--content-font-size": `${
    props.textCardItemSetup?.content?.fontSize ?? 14
  }px`,
}));

const classIcon = computed(() => "text-card-item__dot--sized");

const styleVarsIcon = computed(() => ({
  "--icon-height": `${props.textCardItemSetup?.icon?.height ?? 14}px`,
  "--icon-width": `${props.textCardItemSetup?.icon?.width ?? 14}px`,
}));

const classLine = computed(() => "text-card-item__line-block--sized");

const styleVarsLine = computed(() => ({
  "--line-width": styleVarsIcon.value["--icon-width"],
}));

const markdownToHTML = (text) => {
  if (text == null) return "-";

  const str = typeof text === "string" ? text : String(text);

  try {
    const html = marked.parse(str);
    return DOMPurify.sanitize(html);
  } catch (e) {
    console.error(e);
    return "-";
  }
};

const handleClickHeaderTextCardItem = () => {
  const action = props.textCardItemSetup?.action;
  if (!action || Object.keys(action).length === 0) return;
  const hasLink = !!action.title_link;
  const hasPopup = !!action.title_popup_id;
  if (!hasLink && !hasPopup) return;
  const score = scoreValue.value;

  if (hasLink) {
    const finalLink = MethodService.renderLinkChart(
      {
        value: score,
      },
      action.title_link
    );
    window.open(finalLink);
  } else if (hasPopup) {
    const chartUrl = MethodService.renderLinkChart(
      {
        value: score,
      },null
    );
    emit(
      "showDialogDashboardPopupFromTextCardItem",
      action.title_popup_id,
      chartUrl
    );
  }
};

const handleClickTextCardComponent = () => {
  props.onTextCardClick();
};
onMounted(async () => {
  const code = props.textCardItemData?.[1] ?? "";
  const fileId = await renderIconFileId(code);
  iconSrc.value = renderIconSrc(fileId);
});
</script>

<style lang="scss">
.text-card-item {
  margin-bottom: 25px;

  &__header {
    display: flex;
    align-items: center;
  }

  &__body {
    display: flex;
    align-items: stretch;
    margin-top: 12px;
  }

  &__dot {
    line-height: 1;

    &--sized {
      height: var(--icon-height, 14px);
      width: var(--icon-width, 14px);
    }

    img {
      object-fit: cover;
      height: 100%;
      width: 100%;
    }
  }

  &__title,
  &__content {
    line-height: 1;
  }

  &__title {
    margin-left: 28px;

    &--clickable {
      cursor: pointer;
    }

    font-size: var(--title-font-size, 14px);
    color: var(--title-color, #000);
    font-weight: var(--title-font-weight, normal);
  }

  &__line-block {
    display: flex;
    justify-content: center;
    align-items: stretch;
    height: auto;
    margin-right: 28px;

    &--sized {
      width: var(--line-width, 14px);
      flex-shrink: 0;
    }
  }

  &__line {
    width: 1px;
    background-color: #dcdcdc;
    height: 100%;
    align-items: stretch;
  }

  &__content {
    font-size: var(--content-font-size, 14px);
    font-weight: normal;
    line-height: 1.5;
    p,
    ul {
      margin-bottom: 0;
    }
  }
  pre,
  code,
  kbd,
  samp {
    // font-family: var(--vz-body-font-family);
    font-size: var(--content-font-size, 14px);
    margin-bottom: 0;
    white-space: break-spaces;
  }
}
</style>
